import { CommonActions } from '@bugbug/core/actions/actions';
import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';

import type { MiddlewareAPI } from 'redux';

import { BackendActions } from './backend.actions';

type BackendTypes = keyof typeof BackendActions;
type CommonActionsTypes = keyof typeof CommonActions;

type SupportedBackendActions = {
  [TActionName in BackendTypes]: (
    action: ReturnType<(typeof BackendActions)[TActionName]>,
    listenerApi: MiddlewareAPI,
  ) => void;
};

type SupportedCommonActions = {
  [TActionName in CommonActionsTypes]: (
    action: ReturnType<(typeof CommonActions)[TActionName]>,
    listenerApi: MiddlewareAPI,
  ) => void;
};

export const createExternalActionsListener = ({
  commonActions,
  backendActions,
}: {
  commonActions?: Partial<SupportedCommonActions>;
  backendActions?: Partial<SupportedBackendActions>;
}) => {
  const listenerMiddleware = createListenerMiddleware({ onError: (error) => console.error(error) });

  listenerMiddleware.startListening({
    matcher: isAnyOf(
      ...Object.keys(backendActions ?? {}).map((type) => BackendActions[type]),
      ...Object.keys(commonActions ?? {}).map((type) => CommonActions[type]),
    ),
    effect: async (action, { getState, dispatch }) => {
      const [, actionName] = action.type.split('/');

      const runBackendHandler = backendActions?.[actionName];
      if (runBackendHandler) {
        await runBackendHandler(action, { getState, dispatch });
      }

      const runCommonHandler = commonActions?.[actionName];
      if (runCommonHandler) {
        await runCommonHandler(action, { getState, dispatch });
      }
    },
  });

  return listenerMiddleware;
};
